import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useSechPrice } from "@/hooks/pos/use-sech-price";
import { DrinkComponent, CartItem } from "@/types/pos";
import { convertUsdToSech } from "@/lib/pos/sech-utils";
import { WineIcon, ChevronLeft, Check } from "lucide-react";
import { apiFetch } from "@/utils";

interface DrinkBuilderProps {
	onClose: () => void;
	onAddToCart: (cartItem: CartItem) => void;
	selectedLocation: string;
}

type BuilderStep = "spirit" | "mixers" | "extras";

export default function DrinkBuilder({ onClose, onAddToCart, selectedLocation }: DrinkBuilderProps) {
	const { sechPrice } = useSechPrice();
	const [currentStep, setCurrentStep] = useState<BuilderStep>("spirit");
	const [selectedComponents, setSelectedComponents] = useState<DrinkComponent[]>([]);
	const [drinkName, setDrinkName] = useState("Custom Drink");

	const { data: spirits = [] } = useQuery({
		queryKey: ["/v1/pos/drink-components", { type: "spirit" }],
		queryFn: () => apiFetch('/v1/pos/drink-components?type=spirit', { withCredentials: true }).then(res => res.json()),
	});

	const { data: mixers = [] } = useQuery({
		queryKey: ["/v1/pos/drink-components", { type: "mixer" }],
		queryFn: () => apiFetch('/v1/pos/drink-components?type=mixer', { withCredentials: true }).then(res => res.json()),
	});

	const { data: extras = [] } = useQuery({
		queryKey: ["/v1/pos/drink-components", { type: "extra" }],
		queryFn: () => apiFetch('/v1/pos/drink-components?type=extra', { withCredentials: true }).then(res => res.json()),
	});

	const steps = [
		{ id: "spirit" as BuilderStep, name: "Base", number: 1 },
		{ id: "mixers" as BuilderStep, name: "Mixers", number: 2 },
		{ id: "extras" as BuilderStep, name: "Extras", number: 3 },
	];

	const getCurrentStepIndex = () => steps.findIndex(step => step.id === currentStep);

	const getCurrentComponents = () => {
		switch (currentStep) {
			case "spirit":
				return spirits;
			case "mixers":
				return mixers;
			case "extras":
				return extras;
			default:
				return [];
		}
	};

	const isComponentSelected = (component: DrinkComponent) => {
		return selectedComponents.some(c => c.id === component.id);
	};

	const toggleComponent = (component: DrinkComponent) => {
		if (currentStep === "spirit") {
			// Only one spirit allowed
			const newComponents = selectedComponents.filter(c => c.type !== "spirit");
			setSelectedComponents([...newComponents, component]);
		} else {
			// Multiple mixers/extras allowed
			if (isComponentSelected(component)) {
				setSelectedComponents(selectedComponents.filter(c => c.id !== component.id));
			} else {
				setSelectedComponents([...selectedComponents, component]);
			}
		}
	};

	const getTotalPrice = () => {
		return selectedComponents.reduce((total, component) => {
			return total + parseFloat(component.priceUsd);
		}, 0);
	};

	const canProceed = () => {
		if (currentStep === "spirit") {
			return selectedComponents.some(c => c.type === "spirit");
		}
		return true; // Mixers and extras are optional
	};

	const handleNext = () => {
		const currentIndex = getCurrentStepIndex();
		if (currentIndex < steps.length - 1) {
			setCurrentStep(steps[currentIndex + 1].id);
		}
	};

	const handleBack = () => {
		const currentIndex = getCurrentStepIndex();
		if (currentIndex > 0) {
			setCurrentStep(steps[currentIndex - 1].id);
		} else {
			onClose();
		}
	};

	const handleAddToCart = () => {
		const totalPrice = getTotalPrice();
		const sechAmount = convertUsdToSech(totalPrice, sechPrice);

		const cartItem: CartItem = {
			id: `custom-drink-${Date.now()}`,
			type: "custom-drink",
			customDrink: {
				name: drinkName,
				components: selectedComponents,
			},
			quantity: 1,
			unitPriceUsd: totalPrice,
			unitPriceSech: sechAmount,
			itemLocation: selectedLocation
		};

		onAddToCart(cartItem);
	};

	useEffect(() => {
		// Update drink name based on selected spirit
		const selectedSpirit = selectedComponents.find(c => c.type === "spirit");
		if (selectedSpirit) {
			setDrinkName(`Custom ${selectedSpirit.name} Drink`);
		}
	}, [selectedComponents]);

	const getComponentIcon = (type: string) => {
		switch (type) {
			case "spirit":
				return <WineIcon className="h-6 w-6" />;
			case "mixer":
				return <div className="w-6 h-6 bg-blue-500 rounded-full" />;
			case "extra":
				return <div className="w-6 h-6 bg-green-500 rounded-full" />;
			default:
				return <div className="w-6 h-6 bg-gray-500 rounded-full" />;
		}
	};

	const getComponentColor = (type: string) => {
		switch (type) {
			case "spirit":
				return "bg-[var(--pos-drink-pic-bg-color)] text-amber-600";
			case "mixer":
				return "bg-[var(--pos-drink-pic-bg-color)] text-blue-600";
			case "extra":
				return "bg-[var(--pos-drink-pic-bg-color)] text-green-600";
			default:
				return "bg-[var(--pos-drink-pic-bg-color)] text-gray-600";
		}
	};

	return (
		<div className="inset-0 bg-[var(--pos-secondary-background-color)] max-w-5xl mx-auto z-50 slide-up">
			<div className="">
				{/* Header */}
				<div className="bg-[var(--pos-secondary-background-color)] text-[var(--pos-primary-text-color)]">
					<div className="flex items-center gap-2 pt-[22px] px-5 pb-4">
						<button
							type="button"
							aria-label="Go back"
							onClick={handleBack}
							className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
						>
							<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
						</button>
						<h2 className="font-montserrat text-xl leading-[22px] font-bold text-[var(--pos-primary-text-color)]">Build Your Drink</h2>
					</div>

					{/* Step Indicator */}
					<div className="flex items-center gap-0 px-5 pb-3">
						{steps.map((step, index) => (
							<div key={step.id} className="flex items-center">
								<div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium font-figtree ${
									index < getCurrentStepIndex()
										? "bg-[var(--pos-primary-color)] text-white"
										: index === getCurrentStepIndex()
											? "bg-red-900/30 text-[var(--pos-primary-color)] border border-[var(--pos-primary-color)]"
											: "bg-[var(--pos-primary-background-color)] text-[#97979F]"
								}`}>
									{index < getCurrentStepIndex() ? (
										<Check className="h-3.5 w-3.5" />
									) : (
										<span>{step.number}</span>
									)}
									<span>{step.name}</span>
								</div>
								{index < steps.length - 1 && (
									<div className={`w-4 h-[1px] ${index < getCurrentStepIndex() ? "bg-[var(--pos-primary-color)]" : "bg-[var(--pos-drink-deactive-border-color)]"}`} />
								)}
							</div>
						))}
					</div>
				</div>

				<div className="h-[calc(100vh-148px)] flex flex-col overflow-hidden">
					<div className="flex-1 overflow-auto px-5 flex flex-col gap-2">
						{/* Content */}
						<div>
							<h3 className="font-montserrat text-sm leading-[18px] font-semibold text-[var(--pos-primary-text-color)] mb-3">
								{currentStep === "spirit" && "Choose Your Spirit"}
								{currentStep === "mixers" && "Select Mixers (Optional)"}
								{currentStep === "extras" && "Add Extras (Optional)"}
							</h3>

							<div>
								{getCurrentComponents().map((component: DrinkComponent) => (
									<Card
										key={component.id}
										className={`cursor-pointer transition-all mb-2 last:mb-0 h-[62px] flex items-center px-3 rounded-lg ${isComponentSelected(component)
											? "bg-[var(--pos-primary-background-color)] border border-[var(--pos-primary-color)]"
											: "bg-[var(--pos-primary-background-color)] border border-[var(--pos-layout-border-color)]"
											}`}
										onClick={() => toggleComponent(component)}
									>
										<CardContent className="p-0 flex items-center justify-between w-full">
											<div className="flex items-center justify-between gap-3">
												<div className={`h-[42px] w-[42px] rounded-full flex items-center justify-center ${getComponentColor(component.type)
													}`}>
													{component?.imageUrl && (
														<img src={component?.imageUrl} className="rounded-full" alt="Drink Image" />
													) || getComponentIcon(component.type)}
												</div>
												<div className="flex flex-col gap-[2px] items-start w-[calc(100%-54px)]">
													<h4 className="font-figtree text-[13px] leading-[18px] font-semibold text-[var(--pos-primary-text-color)]">{component.name}</h4>
													<p className="text-[12px] leading-[18px] font-medium text-[var(--pos-secondary-text-color)]">+${component.priceUsd}</p>
												</div>
											</div>
											<div className={`h-4 w-4 flex items-center justify-center border rounded-full ${isComponentSelected(component) ? "border-[var(--pos-primary-color)]" : "border-[var(--pos-radio-btn-border-color)]"}`}>
												<div className={`h-2.5 w-2.5 rounded-full ${isComponentSelected(component) ? "bg-[var(--pos-primary-color)]" : "bg-transparent"}`}></div>
											</div>
										</CardContent>
									</Card>
								))}
							</div>

						</div>

						<div className="pb-[80px]">
							{/* Footer */}
							<div className="bg-[var(--pos-primary-background-color)] rounded-lg p-3 pr-[6px] flex flex-col justify-end">

								{/* Selected Components Summary */}
								{selectedComponents.length > 0 && (
									<div className="">
										<h4 className="font-montserrat text-sm leading-[18px] font-semibold text-[var(--pos-primary-text-color)] mb-3">Selected Components:</h4>
										<div className="h-[94px] overflow-auto mb-3 pr-[6px]">
											{selectedComponents.map((component) => (
												<div key={component.id} className="flex items-center justify-between text-sm mb-3 last:mb-0 pb-3 border-b border-dashed border-[var(--pos-layout-border-color)]">
													<span className="font-figtree text-xs leading-[14px] font-medium text-[var(--pos-secondary-text-color)]">{component.name}</span>
													<span className="font-figtree text-[13px] leading-4 font-semibold text-[var(--pos-primary-text-color)]">${component.priceUsd}</span>
												</div>
											))}
										</div>
									</div>
								)}

								<div className="flex items-center justify-between pr-[6px]">
									<span className="font-montserrat text-sm leading-4 font-semibold text-[var(--pos-primary-text-color)]">Total</span>
									<div className="text-right">
										<span className="font-figtree text-sm leading-4 font-semibold text-[var(--pos-primary-text-color)]">${getTotalPrice().toFixed(2)}</span>
										{sechPrice && (
											<p className="font-figtree text-xs leading-4 font-medium text-[#258750]">
												{convertUsdToSech(getTotalPrice(), sechPrice).toLocaleString(undefined, {
													minimumFractionDigits: 2,
													maximumFractionDigits: 4
												})} SECH
											</p>
										)}
									</div>
								</div>

							</div>
							<div className="">
								{currentStep === "extras" ? (
									<Button
										onClick={handleAddToCart}
										disabled={!canProceed()}
										className="bg-[--pos-primary-color] mt-2 font-figtree w-full text-sm leading-4 font-semibold rounded-lg h-[42px] flex items-center justify-center text-[#FFFFFF] hover:bg-sech-red/90"
									>
										Add to Cart
									</Button>
								) : (
									<Button
										onClick={handleNext}
										disabled={!canProceed()}
										className="bg-[var(--pos-primary-color)] mt-2 font-figtree w-full text-sm leading-4 font-semibold text-[#FFFFFF] rounded-lg h-[42px] flex items-center justify-center hover:bg-sech-red/90"
									>
										Continue to {steps[getCurrentStepIndex() + 1]?.name}
									</Button>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
